import './promiseWithResolvers'
import { IndexedDBStore, type MatrixClient, MemoryStore, createClient } from 'matrix-js-sdk'
import { useAuthStore } from '@/modules/authStore/stores/authStore'
import { getMatrixClientBaseUrl } from '@/modules/chat-matrix-fast/config'
import {
  ensureMatrixSession,
  getMatrixSession,
  isMatrixSessionExpiredOrExpiringSoon,
} from '@/modules/chat-matrix-fast/matrixSession'
import { clearMatrixMediaBlobCache } from '@/modules/chat-matrix-fast/services/matrixMediaUrl'
import { resetMatrixReadReceiptCache } from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'
import {
  type MatrixSyncAuthRecoveryCircuit,
  attachMatrixSyncAuthRecovery,
  getMatrixHttpStatus,
  initMatrixAuthRecovery,
  isMatrixAuthError,
  isMatrixSyncAuthRecoveryBlocked,
  refreshMatrixSessionWithLock,
} from '@/modules/chat-matrix-fast/services/matrixAuthRecovery'
import {
  applyMatrixSdkLogLevel,
  matrixSyncDebugLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { MATRIX_INITIAL_SYNC_LIMIT } from '@/modules/chat-matrix-fast/constants/chat'

// Владелец mutable-состояния клиента: текущий client, syncStarted и
// circuit-счётчики 401-recovery живут только здесь; recovery-модуль получает
// доступ через initMatrixAuthRecovery (см. ниже).
let client: MatrixClient | null = null
let syncStarted = false
let startMatrixClientPromise: Promise<MatrixClient> | null = null
let syncStoreStartupPromise: Promise<void> | null = null

const syncAuthRecoveryCircuit: MatrixSyncAuthRecoveryCircuit = {
  attemptAt: [],
  blockedUntil: 0,
  blockReason: null,
}

initMatrixAuthRecovery({
  circuit: syncAuthRecoveryCircuit,
  getClient: () => client,
  resetMatrixClient,
  setSyncStarted: value => {
    syncStarted = value
  },
})

/** Причина открытого circuit 401-recovery (для логов вне lifecycle-модуля). */
export function getMatrixSyncAuthRecoveryBlockReason(): string | null {
  return syncAuthRecoveryCircuit.blockReason
}

/** Текущий client без создания нового (в отличие от getMatrixClient). */
export function peekMatrixClient(): MatrixClient | null {
  return client
}

type MatrixClientResetHook = () => void

const matrixClientResetHooks: MatrixClientResetHook[] = []

/**
 * Колбэк на resetMatrixClient — для сброса client-scoped кэшей в других модулях
 * без обратного импорта (разрыв цикла matrixChatClient → lifecycle).
 */
export function onMatrixClientReset(hook: MatrixClientResetHook): void {
  matrixClientResetHooks.push(hook)
}

export function isMatrixSyncReady(matrixClient: MatrixClient): boolean {
  const state = matrixClient.getSyncState()
  return state === 'PREPARED' || state === 'SYNCING'
}

function isMatrixClientRunning(matrixClient: MatrixClient | null | undefined): boolean {
  return Boolean(matrixClient?.clientRunning)
}

export function resetMatrixClient(reason = 'unspecified'): void {
  matrixSyncDebugLog('resetMatrixClient', {
    reason,
    hadClient: Boolean(client),
    clientRunning: client?.clientRunning ?? false,
    syncState: client?.getSyncState?.() ?? null,
    syncStarted,
  }, { stack: true })

  if (client) {
    try {
      client.stopClient()
    } catch {
      // already stopped
    }

    if (client.store instanceof IndexedDBStore) {
      void client.store.destroy().catch(() => {})
    }
  }
  client = null
  syncStarted = false
  startMatrixClientPromise = null
  syncStoreStartupPromise = null
  matrixClientResetHooks.forEach(hook => hook())
  // Circuit не сбрасываем здесь: иначе auth-recovery loop обнуляет счётчик
  // через reset+start и снова молотит Synapse. Сброс — на успешный sync
  // или clearMatrixSyncAuthRecoveryCircuit() / полный reload.
  clearMatrixMediaBlobCache()
  void import('@/modules/chat-matrix-fast/services/matrixSygnalPusher')
    .then(({ resetMatrixSygnalPusherState }) => resetMatrixSygnalPusherState())
    .catch(() => {})
  resetMatrixReadReceiptCache()
}

/** Клиент уже в sync или sync ещё идёт — повторный initializeOnce не должен его stop'ать. */
export function isMatrixClientActive(): boolean {
  if (!client) {
    return false
  }

  if (isMatrixSyncReady(client)) {
    return true
  }

  return isMatrixClientRunning(client) && syncStarted
}

function waitForInitialSync(clientToSync: MatrixClient): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (isMatrixSyncReady(clientToSync)) {
      matrixSyncDebugLog('waitForInitialSync → already ready', {
        syncState: clientToSync.getSyncState(),
      })
      resolve()
      return
    }

    const timeout = window.setTimeout(() => {
      if (isMatrixSyncReady(clientToSync)) {
        matrixSyncDebugLog('waitForInitialSync → ready on timeout check', {
          syncState: clientToSync.getSyncState(),
        })
        resolve()
        return
      }
      matrixSyncDebugLog('waitForInitialSync → TIMEOUT', {
        syncState: clientToSync.getSyncState(),
      }, { stack: true })
      reject(new Error('Matrix sync timeout'))
    }, 30_000)

    const onSync = (state: string) => {
      if (state === 'ERROR') {
        const syncData = clientToSync.getSyncStateData?.() as {
          error?: {
            httpStatus?: number
            statusCode?: number
            errcode?: string
            name?: string
          }
        } | null | undefined
        const matrixError = syncData?.error
        const status = matrixError?.httpStatus
          ?? matrixError?.statusCode
          ?? getMatrixHttpStatus(matrixError)
        const errcode = matrixError?.errcode || matrixError?.name
        const isAuthFailure = status === 401
          || status === 403
          || errcode === 'M_UNKNOWN_TOKEN'
          || errcode === 'M_MISSING_TOKEN'

        window.clearTimeout(timeout)
        clientToSync.removeListener('sync' as any, onSync)

        matrixSyncDebugLog('waitForInitialSync → ERROR', {
          status,
          errcode,
          isAuthFailure,
        }, { stack: true })

        const error = new Error('Matrix sync error') as Error & { httpStatus?: number }
        if (isAuthFailure) {
          error.httpStatus = status ?? 401
        }
        reject(error)
        return
      }

      if (state === 'PREPARED' || state === 'SYNCING') {
        window.clearTimeout(timeout)
        clientToSync.removeListener('sync' as any, onSync)
        matrixSyncDebugLog('waitForInitialSync → resolved', { state })
        resolve()
      }
    }

    clientToSync.on('sync' as any, onSync)
  })
}

async function waitForRunningMatrixClient(matrixClient: MatrixClient): Promise<MatrixClient> {
  if (isMatrixSyncReady(matrixClient)) {
    return matrixClient
  }

  try {
    await waitForInitialSync(matrixClient)
  } catch {
  }

  return matrixClient
}

const MATRIX_SYNC_CACHE_DB = 'chat-matrix-sync'

// IndexedDB /sync cache — faster incremental sync on portal reopen.
function createMatrixSyncStore(userId: string): IndexedDBStore | undefined {
  if (typeof indexedDB === 'undefined') {
    return undefined
  }

  return new IndexedDBStore({
    indexedDB,
    dbName: `${MATRIX_SYNC_CACHE_DB}:${userId}`,
  })
}

export function deleteMatrixSyncCache(userId: string): void {
  if (typeof indexedDB === 'undefined') {
    return
  }

  // LocalIndexedDBStoreBackend хранит базу с префиксом matrix-js-sdk:
  indexedDB.deleteDatabase(`matrix-js-sdk:${MATRIX_SYNC_CACHE_DB}:${userId}`)
}

/** IndexedDB-стор обязан подняться до startClient; при сбое — MemoryStore без кэша. */
async function ensureMatrixSyncStoreReady(matrixClient: MatrixClient): Promise<void> {
  const store = matrixClient.store
  if (!(store instanceof IndexedDBStore)) {
    return
  }

  if (!syncStoreStartupPromise) {
    syncStoreStartupPromise = store.startup().catch(error => {
      matrixSyncDebugLog('ensureMatrixSyncStoreReady → fallback to MemoryStore', {
        error: error instanceof Error ? error.message : String(error),
      })
      matrixClient.store = new MemoryStore({ localStorage: window.localStorage })
    })
  }

  await syncStoreStartupPromise
}

function requireMatrixSession() {
  const session = getMatrixSession()
  if (!session?.accessToken || !session.userId) {
    throw new Error('Нет сессии чата. Обновите страницу.')
  }

  return session
}

export function getMatrixClient(): MatrixClient {
  if (client) {
    return client
  }

  const session = requireMatrixSession()

  applyMatrixSdkLogLevel()

  client = createClient({
    baseUrl: getMatrixClientBaseUrl(),
    accessToken: session.accessToken,
    userId: session.userId,
    deviceId: session.deviceId,
    disableVoip: true,
    timelineSupport: true,
    store: createMatrixSyncStore(session.userId),
  })

  return client
}

function attachMatrixSyncStateLogger(matrixClient: MatrixClient): void {
  const debugClient = matrixClient as MatrixClient & {
    __syncDebugAttached?: boolean
    __stopClientWrapped?: boolean
  }

  if (!debugClient.__stopClientWrapped) {
    debugClient.__stopClientWrapped = true
    const originalStopClient = matrixClient.stopClient.bind(matrixClient)
    matrixClient.stopClient = () => {
      matrixSyncDebugLog('MatrixClient.stopClient()', {
        syncState: matrixClient.getSyncState(),
        clientRunning: matrixClient.clientRunning,
        syncStarted,
      }, { stack: true })
      return originalStopClient()
    }
  }

  if (debugClient.__syncDebugAttached) {
    return
  }

  debugClient.__syncDebugAttached = true

  matrixClient.on('sync' as any, (state: string, prevState: string | null) => {
    matrixSyncDebugLog('sdk sync state', {
      state,
      prevState,
      clientRunning: matrixClient.clientRunning,
      syncStarted,
    })
  })

  attachMatrixSyncAuthRecovery(matrixClient)
}

export async function startMatrixClient(): Promise<MatrixClient> {
  if (!useAuthStore().isAuthenticated) {
    if (client) {
      return client
    }
    throw new Error('not authenticated')
  }

  if (isMatrixSyncAuthRecoveryBlocked()) {
    const detail = syncAuthRecoveryCircuit.blockReason || 'homeserver отклоняет Matrix-токен (401)'
    matrixSyncDebugLog('startMatrixClient → blocked by auth recovery circuit', {
      detail,
      blockedUntil: syncAuthRecoveryCircuit.blockedUntil,
    })
    throw new Error(
      `Чат временно остановлен: ${detail}. Повтор через несколько минут или обновите страницу после проверки Synapse.`,
    )
  }

  if (client && isMatrixSyncReady(client)) {
    const session = getMatrixSession()
    if (session && isMatrixSessionExpiredOrExpiringSoon(session)) {
      matrixSyncDebugLog('startMatrixClient → refresh expiring token before reuse')
      await refreshMatrixSessionWithLock()
    }
    matrixSyncDebugLog('startMatrixClient → reuse ready client', {
      syncState: client.getSyncState(),
    })
    return client
  }

  if (startMatrixClientPromise) {
    matrixSyncDebugLog('startMatrixClient → join in-flight start')
    return startMatrixClientPromise
  }

  if (client && isMatrixClientRunning(client) && syncStarted) {
    matrixSyncDebugLog('startMatrixClient → wait for running client', {
      syncState: client.getSyncState(),
    })
    startMatrixClientPromise = waitForRunningMatrixClient(client)
      .finally(() => {
        startMatrixClientPromise = null
      })
    return startMatrixClientPromise
  }

  matrixSyncDebugLog('startMatrixClient → begin', {
    syncStarted,
    hadClient: Boolean(client),
    syncState: client?.getSyncState?.() ?? null,
  }, { stack: true })

  startMatrixClientPromise = (async () => {
    const session = await ensureMatrixSession()
    if (!session) {
      throw new Error('Нет сессии чата. Обновите страницу.')
    }

    let matrixClient = getMatrixClient()
    matrixClient.setAccessToken(session.accessToken)
    attachMatrixSyncStateLogger(matrixClient)

    if (syncStarted && isMatrixSyncReady(matrixClient)) {
      matrixSyncDebugLog('startMatrixClient → already ready after session')
      return matrixClient
    }

    const attemptStart = async (clientToStart: MatrixClient, label: string) => {
      const latest = getMatrixSession()
      if (latest?.accessToken) {
        clientToStart.setAccessToken(latest.accessToken)
      }

      if (clientToStart.clientRunning && isMatrixSyncReady(clientToStart)) {
        syncStarted = true
        matrixSyncDebugLog(`attemptStart[${label}] → already running+ready`)
        return
      }

      await ensureMatrixSyncStoreReady(clientToStart)

      const readyPromise = waitForInitialSync(clientToStart)
      syncStarted = true

      if (!clientToStart.clientRunning) {
        matrixSyncDebugLog(`attemptStart[${label}] → startClient()`, {
          syncState: clientToStart.getSyncState(),
        })
        await clientToStart.startClient({ initialSyncLimit: MATRIX_INITIAL_SYNC_LIMIT })
      } else {
        matrixSyncDebugLog(`attemptStart[${label}] → clientRunning, wait only`, {
          syncState: clientToStart.getSyncState(),
        })
      }

      if (isMatrixSyncReady(clientToStart)) {
        matrixSyncDebugLog(`attemptStart[${label}] → ready after startClient`)
        return
      }

      await readyPromise
      matrixSyncDebugLog(`attemptStart[${label}] → ready after wait`)
    }

    const stopClientForRetry = (reason: string) => {
      matrixSyncDebugLog('stopClient', {
        reason,
        syncState: matrixClient.getSyncState(),
        clientRunning: matrixClient.clientRunning,
      }, { stack: true })
      try {
        matrixClient.stopClient()
      } catch {
        // already stopped
      }
    }

    try {
      await attemptStart(matrixClient, 'initial')
    } catch (error) {
      if (isMatrixSyncReady(matrixClient)) {
        syncStarted = true
        matrixSyncDebugLog('startMatrixClient → error ignored, sync already ready', {
          error: error instanceof Error ? error.message : String(error),
        })
        return matrixClient
      }

      syncStarted = false
      stopClientForRetry(`start-failed: ${error instanceof Error ? error.message : String(error)}`)

      if (!isMatrixAuthError(error)) {
        throw error
      }

      try {
        matrixSyncDebugLog('startMatrixClient → auth retry after refresh')
        await refreshMatrixSessionWithLock()
        matrixClient = getMatrixClient()
        attachMatrixSyncStateLogger(matrixClient)
        await attemptStart(matrixClient, 'auth-retry')
      } catch (retryError) {
        if (isMatrixSyncReady(matrixClient)) {
          syncStarted = true
          matrixSyncDebugLog('startMatrixClient → retry error ignored, sync ready')
          return matrixClient
        }

        syncStarted = false
        stopClientForRetry(`auth-retry-failed: ${retryError instanceof Error ? retryError.message : String(retryError)}`)

        if (!isMatrixAuthError(retryError)) {
          throw retryError
        }

        resetMatrixClient('force-new-session-after-auth-retry')
        const forced = await ensureMatrixSession({ force: true })
        if (!forced) {
          throw new Error('Не удалось восстановить сессию чата')
        }

        matrixClient = getMatrixClient()
        attachMatrixSyncStateLogger(matrixClient)
        try {
          await attemptStart(matrixClient, 'force-session')
        } catch (finalError) {
          if (isMatrixSyncReady(matrixClient)) {
            syncStarted = true
            return matrixClient
          }
          syncStarted = false
          throw finalError
        }
      }
    }

    matrixSyncDebugLog('startMatrixClient → done', {
      syncState: getMatrixClient().getSyncState(),
      syncStarted,
    })
    return getMatrixClient()
  })().finally(() => {
    startMatrixClientPromise = null
  })

  return startMatrixClientPromise
}
